import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { Loader2, GitBranch, BarChart3, Database, Sparkles } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, Legend, RadarChart, PolarGrid, PolarAngleAxis, Radar } from "recharts";
import { Shimmer } from "../common/DashboardSkeleton";
import { usePrefetchOrFetch } from "../common/DataPrefetch";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const OC = { care: "#10b981", utility: "#3b82f6", creative: "#a855f7", extraction: "#ef4444" };

/* ─── Reveal ─── */
const Reveal = ({ children, delay = 0 }) => {
  const [vis, setVis] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setVis(true); obs.disconnect(); } }, { threshold: 0.1 });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return (
    <div ref={ref} style={{ opacity: vis ? 1 : 0, transform: vis ? "translateY(0)" : "translateY(20px)", transition: `all 0.6s ease ${delay}ms` }}>
      {children}
    </div>
  );
};

/* ─── Human-readable field names ─── */
const FIELD_NAMES = {
  "cs.AI": "AI General", "cs.LG": "Machine Learning", "cs.CV": "Computer Vision",
  "cs.CL": "NLP & Language", "cs.RO": "Robotics", "cs.CR": "Cryptography & Security",
  "cs.SE": "Software Engineering", "cs.DC": "Distributed Computing", "cs.HC": "Human-Computer Interaction",
  "cs.IR": "Information Retrieval", "cs.NE": "Neural & Evolutionary", "cs.CY": "Computers & Society",
  "cs.PL": "Programming Languages", "cs.GR": "Graphics", "cs.MM": "Multimedia",
  "cs.SD": "Sound", "cs.DB": "Databases", "stat.ML": "Statistical ML",
  "eess.SY": "Systems & Control", "eess.SP": "Signal Processing", "eess.IV": "Image & Video",
  "Computer Science": "Computer Science", "Medicine": "Medicine", "Mathematics": "Mathematics",
  "Engineering": "Engineering", "Physics": "Physics", "Biology": "Biology",
  "Environmental Science": "Environmental Science",
};

export const ResearchFields = () => {
  const [fieldsData, fieldsLoading] = usePrefetchOrFetch("researchFields", `${API}/research/fields`);
  const fields = fieldsData?.fields || null;
  const [sources, setSources] = useState(null);
  const [sourcesLoading, setSourcesLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/research/source-comparison`)
      .then(r => setSources(r.data.sources))
      .catch(() => {})
      .finally(() => setSourcesLoading(false));
  }, []);

  if (fieldsLoading && !fields) {
    return (
      <div className="space-y-5 animate-fade-in" data-testid="research-fields-loading">
        <div className="bg-white/[0.02] border border-white/[0.06] rounded-xl p-5 space-y-3">
          <Shimmer className="h-3 w-48" />
          <Shimmer className="h-5 w-72" />
          <Shimmer className="h-3 w-56" />
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          {[1, 2].map(i => (
            <div key={i} className="bg-white/[0.02] border border-white/[0.06] rounded-xl p-5">
              <Shimmer className="h-3 w-40 mb-4" />
              <div className="h-56 flex items-end gap-2 px-2">
                {[60, 45, 70, 35, 55, 50, 40, 65].map((h, j) => (
                  <Shimmer key={j} className="flex-1 rounded" style={{ height: `${h}%` }} />
                ))}
              </div>
            </div>
          ))}
        </div>
        <p className="text-center text-[10px] text-slate-500 animate-pulse">Analyzing research fields across 1M+ papers...</p>
      </div>
    );
  }

  // Defensive shape normalization — bootstrap historically used `count` while the
  // live endpoint returns `total/utility/creative/extraction`. Coalesce to one shape.
  const fieldData = (fields || []).map(f => {
    const total = f.total ?? f.count ?? 0;
    const care = f.care ?? 0;
    const utility = f.utility ?? Math.max(total - care - (f.creative ?? 0) - (f.extraction ?? 0), 0);
    const creative = f.creative ?? 0;
    const extraction = f.extraction ?? 0;
    return {
      ...f,
      total,
      care,
      utility,
      creative,
      extraction,
      care_pct: f.care_pct ?? (total ? Math.round((care / total) * 1000) / 10 : 0),
      avg_citations: f.avg_citations ?? 0,
      label: FIELD_NAMES[f.field] || f.field,
    };
  });

  const topFields = fieldData.slice(0, 12);
  const radarData = fieldData.slice(0, 8).map(f => ({
    field: f.label.length > 12 ? f.label.slice(0, 12) + "..." : f.label,
    care: f.care,
    utility: f.utility,
    extraction: f.extraction,
  }));

  const s2 = sources?.semantic_scholar;
  const arxiv = sources?.arxiv;
  const hf = sources?.huggingface;
  const ssrn = sources?.ssrn;
  const hai = sources?.stanford_hai;

  const sourceCards = [
    s2 && ["Semantic Scholar", s2, "#06b6d4"],
    arxiv && ["arXiv", arxiv, "#f59e0b"],
    hf && ["HuggingFace", hf, "#a855f7"],
    ssrn && ["SSRN", ssrn, "#22c55e"],
    hai && ["Stanford HAI", hai, "#ef4444"],
  ].filter(Boolean);

  return (
    <div className="space-y-6" data-testid="research-fields">
      {/* Header */}
      <Reveal>
        <div className="flex items-center gap-3">
          <GitBranch className="w-5 h-5 text-indigo-400" />
          <div>
            <h2 className="text-lg font-bold text-white">Research Landscape</h2>
            <p className="text-[10px] text-slate-500">Field distribution, source comparison, and orientation analysis</p>
          </div>
        </div>
      </Reveal>

      {/* Source Comparison Cards */}
      {sourceCards.length > 0 && (
        <Reveal delay={100}>
          <div className={`grid grid-cols-1 ${sourceCards.length >= 3 ? 'md:grid-cols-3' : 'md:grid-cols-2'} gap-4`}>
            {sourceCards.map(([name, data, color]) => (
              <div key={name} className="bg-white/[0.02] border border-white/[0.06] rounded-xl p-5"
                style={{ borderColor: `${color}15` }}>
                <div className="flex items-center gap-2 mb-3">
                  <Database className="w-4 h-4" style={{ color }} />
                  <p className="text-xs font-bold text-white">{name}</p>
                  <span className="ml-auto text-lg font-black" style={{ color }}>{data.total.toLocaleString()}</span>
                </div>
                <div className="grid grid-cols-4 gap-2">
                  <div className="text-center">
                    <p className="text-sm font-black text-emerald-400">{data.care_pct}%</p>
                    <p className="text-[8px] text-slate-500">Care</p>
                  </div>
                  <div className="text-center">
                    <p className="text-sm font-black text-cyan-400">{data.avg_citations}</p>
                    <p className="text-[8px] text-slate-500">Avg Cites</p>
                  </div>
                  <div className="text-center">
                    <p className="text-sm font-black text-amber-400">{data.ethical_pct}%</p>
                    <p className="text-[8px] text-slate-500">Ethical</p>
                  </div>
                  <div className="text-center">
                    <p className="text-sm font-black text-indigo-400">{data.geo_coverage_pct}%</p>
                    <p className="text-[8px] text-slate-500">Geo Data</p>
                  </div>
                </div>
                {/* Mini orientation bar */}
                <div className="flex h-2 rounded-full overflow-hidden mt-3 gap-0.5">
                  {Object.entries(data.orientation || {}).filter(([k]) => k !== "unclassified").map(([k, v]) => (
                    <div key={k} style={{ width: `${v / data.total * 100}%`, background: OC[k] || "#475569" }} className="rounded-full" />
                  ))}
                </div>
              </div>
            ))}
          </div>
        </Reveal>
      )}

      {/* Top Fields Bar Chart */}
      {topFields.length > 0 && (
        <Reveal delay={150}>
          <div className="bg-white/[0.02] border border-white/[0.06] rounded-xl p-5">
            <div className="flex items-center gap-2 mb-4">
              <BarChart3 className="w-4 h-4 text-indigo-400" />
              <p className="text-[10px] font-bold text-indigo-400/70 uppercase tracking-[0.2em]">Top Research Fields by Paper Count</p>
            </div>
            <ResponsiveContainer width="100%" height={350}>
              <BarChart data={topFields} layout="vertical" margin={{ left: 120, right: 20 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" />
                <XAxis type="number" tick={{ fill: "#64748b", fontSize: 10 }} />
                <YAxis type="category" dataKey="label" tick={{ fill: "#94a3b8", fontSize: 9 }} width={115} />
                <Tooltip contentStyle={{ background: "#0f172a", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, fontSize: 11 }} />
                <Legend wrapperStyle={{ fontSize: 10 }} />
                <Bar dataKey="care" stackId="a" fill="#10b981" name="Care" />
                <Bar dataKey="utility" stackId="a" fill="#3b82f6" name="Utility" />
                <Bar dataKey="creative" stackId="a" fill="#a855f7" name="Creative" />
                <Bar dataKey="extraction" stackId="a" fill="#ef4444" name="Extraction" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Reveal>
      )}

      {/* Radar: Orientation Profile by Field */}
      {radarData.length > 0 && (
        <Reveal delay={200}>
          <div className="bg-white/[0.02] border border-white/[0.06] rounded-xl p-5">
            <div className="flex items-center gap-2 mb-4">
              <Sparkles className="w-4 h-4 text-violet-400" />
              <p className="text-[10px] font-bold text-violet-400/70 uppercase tracking-[0.2em]">Orientation Profile by Research Field</p>
            </div>
            <div className="flex justify-center">
              <ResponsiveContainer width="100%" height={350}>
                <RadarChart data={radarData} cx="50%" cy="50%" outerRadius="70%">
                  <PolarGrid stroke="rgba(255,255,255,0.06)" />
                  <PolarAngleAxis dataKey="field" tick={{ fill: "#94a3b8", fontSize: 9 }} />
                  <Radar name="Care" dataKey="care" stroke="#10b981" fill="#10b981" fillOpacity={0.2} strokeWidth={2} />
                  <Radar name="Utility" dataKey="utility" stroke="#3b82f6" fill="#3b82f6" fillOpacity={0.15} strokeWidth={2} />
                  <Radar name="Extraction" dataKey="extraction" stroke="#ef4444" fill="#ef4444" fillOpacity={0.1} strokeWidth={2} />
                  <Legend wrapperStyle={{ fontSize: 10 }} />
                  <Tooltip contentStyle={{ background: "#0f172a", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, fontSize: 11 }} />
                </RadarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </Reveal>
      )}

      {/* Field Details Grid */}
      {fieldData.length > 0 && (
        <Reveal delay={250}>
          <div className="bg-white/[0.02] border border-white/[0.06] rounded-xl p-5">
            <p className="text-[10px] font-bold text-slate-500 uppercase tracking-[0.2em] mb-4">All Research Fields</p>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
              {fieldData.map((f) => (
                <div key={f.field} className="bg-white/[0.01] border border-white/[0.04] rounded-lg p-3 hover:bg-white/[0.03] transition-all">
                  <p className="text-[10px] font-bold text-white mb-1 truncate">{f.label}</p>
                  <p className="text-[8px] text-slate-500 mb-2">{f.field}</p>
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-black text-indigo-400">{f.total.toLocaleString()}</span>
                    <span className="text-[9px] text-emerald-400">{f.care_pct}% care</span>
                  </div>
                  <div className="flex h-1.5 rounded-full overflow-hidden mt-2 gap-0.5">
                    <div style={{ width: `${f.care / f.total * 100}%` }} className="bg-emerald-500 rounded-full" />
                    <div style={{ width: `${f.utility / f.total * 100}%` }} className="bg-blue-500 rounded-full" />
                    <div style={{ width: `${f.creative / f.total * 100}%` }} className="bg-purple-500 rounded-full" />
                    <div style={{ width: `${f.extraction / f.total * 100}%` }} className="bg-red-500 rounded-full" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </Reveal>
      )}
    </div>
  );
};
